import { inject, Injectable, signal } from '@angular/core';
import {
    FeatureFlagController,
    FeedFilterTypeEnum,
    FeedItemEntity,
    OrderController,
} from '@domain';
import { catchError, map, Observable, of } from 'rxjs';
import { PaginatedEntity, PaginationParams } from 'src/app/domain/DTOs/paginate.entity';

/**
 * Estado do FEED unificado da home (feature `unified_feed`): serviços (GROUP)
 * e documentos avulsos (DOCUMENT) numa lista só, estilo WhatsApp. Espelha o
 * [OrderMainService] (signals + paginação acumulada), trocando a fonte para
 * `GET /order/feed` + a aba Tudo/Serviços/Avulsos.
 */
@Injectable({
    providedIn: 'root',
})
export class FeedMainService {
    public loading = signal<boolean>(false);
    public paginate = signal<PaginatedEntity<FeedItemEntity>>(null);
    public items = signal<FeedItemEntity[]>([]);
    public filterType = signal<FeedFilterTypeEnum>(FeedFilterTypeEnum.ALL);

    /**
     * Grupo selecionado no trilho da lista: `null` = Todos (lista seccionada por
     * grupo), `'none'` = só os itens sem grupo, ou o id de um grupo do usuário.
     * O filtro é feito no backend, então cada troca recarrega o feed.
     */
    public groupId = signal<string | null>(null);

    /**
     * O feed já carregou com sucesso ao menos uma vez? A tela de boas-vindas
     * (order-welcome) só pode aparecer depois disso — sem esta guarda, o estado
     * inicial (items vazio antes da 1ª busca) seria confundido com "conta vazia".
     */
    public loadedOnce = signal<boolean>(false);

    /**
     * Última busca aplicada — reutilizada pelos refreshes silenciosos e pela
     * page para distinguir "conta vazia" (welcome) de "busca sem resultados".
     */
    public lastTextSearch = signal<string | undefined>(undefined);

    /**
     * Pedido de abertura do chat de IA vindo de fora da bolinha (card "Criar com
     * IA" do modal "+ Novo" e tela de boas-vindas). O pedido nasce no feed-list ou
     * na welcome; a bolinha vive na page, que observa este tick e abre o painel.
     * `chatOpenStandalone` diz o modo: já em documento avulso (sem "onde salvar?")
     * ou sem destino (o chat pergunta, com a opção de avulso).
     */
    public chatOpenTick = signal<number>(0);

    /**
     * Pedidos de criação vindos de FORA da lista (hoje a tela sem seleção).
     *
     * Os modais de criar ordem, escolher tipo de documento e importar vivem no
     * `feed-list`; duplicá-los em cada tela que quer criar algo seria duplicar também o
     * pós-criação. O tick é o mesmo padrão do chat: quem pede incrementa, o feed-list
     * consome e zera.
     */
    public newServiceTick = signal<number>(0);
    public importTick = signal<number>(0);
    /** Pedido do wizard "Criar com IA" (o "+" da lista): quem abre é a page. */
    public iaWizardTick = signal<number>(0);
    public chatOpenStandalone = signal<boolean>(false);

    /** Sequência das buscas visíveis: só a resposta da última entra na lista. */
    private _requestSeq = 0;

    // * [Injects]
    private _orderController = inject(OrderController);
    private _featureFlagController = inject(FeatureFlagController);

    /**
     * Busca o feed
     * @param textSearch Busca textual
     * @param page Página (1 substitui a lista; >1 acumula)
     * @param silent Atualiza sem acender o loading (polling de itens em geração)
     */
    public getFeed(
        textSearch?: string,
        page?: number,
        silent: boolean = false
    ): Observable<PaginatedEntity<FeedItemEntity>> {
        this.lastTextSearch.set(textSearch);
        if (!silent) this.loading.set(true);

        // Busca visível nova invalida as anteriores (troca de aba/busca rápida: a
        // resposta que chega por último não pode ganhar). A silenciosa não invalida
        // nada, só é descartada se uma visível começar depois dela.
        const seq = silent ? this._requestSeq : ++this._requestSeq;

        // 20 por página: com 10, em telas grandes a lista não enchia a coluna e o
        // "carregar mais" aparecia no meio do painel vazio.
        const pageSize = 20;
        const paginate: PaginationParams = {
            page: page ?? this.paginate()?.page ?? 1,
            limit: pageSize,
        };

        // Refresh silencioso da página 1 com mais páginas carregadas: busca todas de
        // uma vez, senão a lista encolhia para a primeira página e a rolagem pulava.
        const loadedPages = this.paginate()?.page ?? 1;
        const refetchLoaded = silent && paginate.page == 1 && loadedPages > 1;
        if (refetchLoaded) paginate.limit = loadedPages * pageSize;

        return this._orderController
            .feed(paginate, textSearch, this.filterType(), false, this.groupId())
            .pipe(
                map(paginate => {
                    // Resposta velha: outra busca visível já foi pedida depois
                    if (seq !== this._requestSeq || (silent && this.loading())) return paginate;

                    // Volta ao tamanho de página normal, para o "carregar mais" seguir de onde parou
                    if (refetchLoaded) {
                        paginate = {
                            ...paginate,
                            page: loadedPages,
                            limit: pageSize,
                            totalPages: Math.ceil(paginate.total / pageSize),
                        };
                    }

                    this.paginate.set(paginate);

                    // Primeira página substitui; demais acumulam (load more)
                    if (paginate.page == 1 || refetchLoaded || this.items().length == 0) {
                        this.items.set(paginate.data);
                    } else {
                        this.items.update(items => [...items, ...paginate.data]);
                    }

                    if (!silent) this.loading.set(false);
                    this.loadedOnce.set(true);

                    return paginate;
                }),
                catchError(() => {
                    if (!silent && seq === this._requestSeq) this.loading.set(false);

                    return of(null);
                })
            );
    }

    /** Troca o grupo do trilho. O chamador dispara o reload. */
    public setGroup(groupId: string | null): boolean {
        if (this.groupId() === groupId) return false;
        this.groupId.set(groupId);

        return true;
    }

    /**
     * Revalida o feed em silêncio (sem shimmer), mantendo a busca atual.
     * Gancho pós-mutação de order (membros/descrição/arquivar/remover): os
     * itens do feed são um cache — sem isso, o card do grupo fica desatualizado
     * até o próximo reload. No-op para quem está fora do beta do feed.
     */
    public refreshSilent(): void {
        if (this._featureFlagController.isBlocked('unified_feed')) return;
        this.getFeed(this.lastTextSearch(), 1, true).subscribe();
    }

    /** Troca a aba (Tudo/Serviços/Avulsos). O chamador dispara o reload. */
    public setFilter(type: FeedFilterTypeEnum): boolean {
        if (this.filterType() === type) return false;
        this.filterType.set(type);

        return true;
    }

    /** Pede a abertura do chat de IA sem destino: o chat pergunta onde salvar. */
    public requestChat(): void {
        this.chatOpenStandalone.set(false);
        this.chatOpenTick.update(tick => tick + 1);
    }

    /** Pede a abertura do chat de IA já em modo documento avulso. */
    public requestChatStandalone(): void {
        this.chatOpenStandalone.set(true);
        this.chatOpenTick.update(tick => tick + 1);
    }

    /** Pede o modal de nova ordem de serviço (quem abre é o feed-list). */
    public requestNewService(): void {
        this.newServiceTick.update(tick => tick + 1);
    }

    /** Pede o wizard "Criar com IA" (tipo → modelo → contexto). */
    public requestIaWizard(): void {
        this.iaWizardTick.update(tick => tick + 1);
    }

    /** Novo documento avulso: é o mesmo wizard (tipo → modelo ou em branco). */
    public requestNewDocument(): void {
        this.requestIaWizard();
    }

    /** Pede o modal de importar arquivos. */
    public requestImport(): void {
        this.importTick.update(tick => tick + 1);
    }
}
